/**
 * The unlocked-vault singleton: what is in memory, for how long, and where it
 * is allowed to be persisted.
 *
 * Authentication and decryption are separate states here, and keeping them
 * separate is the point. The session cookie proves *who* you are; it does not
 * and cannot open the vault, because the server would have to hold a key for
 * that to work. So a signed-in user with a locked vault sees the app chrome and
 * no file names — which is also the honest picture of what the server knows.
 *
 * Three rules govern storage, and all three exist because of XSS:
 *
 *  1. **Keys are non-extractable `CryptoKey` objects.** Once WebCrypto imports
 *     bytes with `extractable: false`, script cannot read them back. Injected
 *     script can still *use* the key while the tab is open — that is
 *     unavoidable in a web app — but it cannot copy it out and keep it.
 *
 *  2. **Never `localStorage` or `sessionStorage`.** Those hold strings, and a
 *     string is exfiltrated in one line. "Trust this device" persists the
 *     non-extractable `CryptoKey` in IndexedDB instead, which is the one place
 *     a browser will store a key object without ever making its bytes
 *     available again.
 *
 *  3. **Auto-lock on idle**, unless the device is trusted. Bounded exposure
 *     for an unattended laptop, and it drops the keys from memory rather than
 *     merely hiding the UI. A trusted device keeps its key in IndexedDB, so a
 *     refresh would unlock it again and the lock would protect nothing.
 */

import type { UnlockedVault, VaultBlobs } from './keys';
import {
  unlockWithMasterKey,
  unlockWithPassword,
  unlockWithRecovery,
  VaultLockedError
} from './keys';
import type { Bytes } from './primitives';

const DB_NAME = 'mauvely-cloud';
const DB_VERSION = 1;
const STORE = 'vault-keys';
/** The account is already scoped by origin and session. */
const RECORD_KEY = 'device-master-key';
/**
 * Set by an explicit lock on a trusted device, cleared by the next unlock.
 *
 * The stored key is what trust *is*, so a lock cannot delete it without
 * becoming "forget this device". Without this mark the restore on page load
 * used the key regardless, and the next refresh undid "Lock files".
 */
const HELD_KEY = 'locked-by-user';

/** Idle time before the vault locks itself. */
export const AUTO_LOCK_MS = 30 * 60 * 1000;

type Listener = (state: VaultState) => void;

export type VaultState =
  | { status: 'locked' }
  | { status: 'unlocked'; vault: UnlockedVault; trusted: boolean };

let state: VaultState = { status: 'locked' };
let idleTimer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<Listener>();

export const getVaultState = (): VaultState => state;

/**
 * The unlocked vault, or throw.
 *
 * Everything that touches a key goes through here, so there is exactly one
 * place that decides what "locked" means and no path that silently proceeds
 * with a half-open vault.
 */
export function requireVault(): UnlockedVault {
  if (state.status !== 'unlocked') throw new VaultLockedError();
  touch();
  return state.vault;
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function publish(next: VaultState): void {
  state = next;
  for (const l of listeners) l(next);
}

// ── unlocking ───────────────────────────────────────────────────────────────

export async function unlock(
  secret: { password: string } | { entropy: Bytes },
  blobs: VaultBlobs,
  options: { trustDevice?: boolean } = {}
): Promise<UnlockedVault> {
  const vault =
    'password' in secret
      ? await unlockWithPassword(secret.password, blobs)
      : await unlockWithRecovery(secret.entropy, blobs);

  await adopt(vault, options.trustDevice ?? false);
  return vault;
}

/**
 * Unlock with a master key a passkey already unwrapped.
 *
 * The passkey path produces the same non-extractable master key the password
 * path does, so from here down nothing differs — this is `unlockWithMasterKey`
 * plus adoption, exactly like the trusted-device restore. Keeping it as its own
 * named entry point is what stops the unlock screen reaching into
 * `restoreTrusted`, which means something else.
 */
export async function unlockWithMaster(
  masterKey: CryptoKey,
  blobs: VaultBlobs,
  options: { trustDevice?: boolean } = {}
): Promise<UnlockedVault> {
  const vault = await unlockWithMasterKey(masterKey, blobs);
  await adopt(vault, options.trustDevice ?? false);
  return vault;
}

/** Adopt an already-unlocked vault — the path `createVault` takes at first run,
 *  where there is nothing to unlock because it was just built. */
export async function adopt(vault: UnlockedVault, trustDevice = false): Promise<void> {
  publish({ status: 'unlocked', vault, trusted: trustDevice });
  startIdleWatch();

  if (trustDevice) {
    // Best-effort: a browser in private mode, or one with storage blocked, will
    // reject this. The vault is open either way — the user just re-enters their
    // password next time, which is a worse experience and not a broken one.
    try {
      await persistKey(vault);
      // A failed release leaves the device held: locked on the next load,
      // which is the safe way round.
      await release().catch(() => {});
    } catch {
      publish({ status: 'unlocked', vault, trusted: false });
      touch();
    }
  } else {
    await forgetKey().catch(() => {});
  }
}

/**
 * Restore a trusted-device vault without a password.
 *
 * Only the master key is persisted; the private key is unwrapped from the
 * server's blob on each restore. That keeps IndexedDB to one object and means a
 * key rotation server-side cannot be shadowed by a stale local copy.
 */
export async function restoreTrusted(blobs: VaultBlobs): Promise<UnlockedVault | null> {
  if (await isHeld()) return null;
  const masterKey = await readKey().catch(() => null);
  if (!masterKey) return null;

  try {
    const vault = await unlockWithMasterKey(masterKey, blobs);
    publish({ status: 'unlocked', vault, trusted: true });
    startIdleWatch();
    return vault;
  } catch {
    // The stored key no longer opens this vault — the password was changed on
    // another device, or the account was reset. Drop it rather than retrying.
    await forgetKey().catch(() => {});
    return null;
  }
}

/**
 * Lock the vault and drop every key from memory. Idempotent.
 *
 * The keys are gone before this resolves anything. What it then waits for is
 * the mark that keeps a trusted device locked through a refresh — see
 * `HELD_KEY`. On a device that stores no key the mark is harmless, and the next
 * unlock clears it either way.
 */
export async function lock(): Promise<void> {
  stopIdleWatch();
  publish({ status: 'locked' });
  await hold().catch(() => {});
}

/** Lock and also forget a trusted device. Used by sign-out and by "forget this
 *  device" — a locked vault that is still trusted would unlock itself again. */
export async function signOut(): Promise<void> {
  stopIdleWatch();
  publish({ status: 'locked' });
  await forgetKey().catch(() => {});
}

// ── idle auto-lock ──────────────────────────────────────────────────────────

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'visibilitychange'] as const;

function startIdleWatch(): void {
  if (typeof window === 'undefined') return;
  for (const e of ACTIVITY_EVENTS) window.addEventListener(e, touch, { passive: true });
  touch();
}

function stopIdleWatch(): void {
  if (typeof window === 'undefined') return;
  for (const e of ACTIVITY_EVENTS) window.removeEventListener(e, touch);
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = undefined;
}

function touch(): void {
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = undefined;
  if (state.status !== 'unlocked' || state.trusted) return;
  idleTimer = setTimeout(() => void lock(), AUTO_LOCK_MS);
}

// ── IndexedDB ───────────────────────────────────────────────────────────────

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transact<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>) {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const request = run(tx.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        tx.oncomplete = () => db.close();
      })
  );
}

/**
 * Store the master key for "trust this device".
 *
 * IndexedDB is the only web storage that accepts a `CryptoKey` as a structured
 * clone, and it preserves `extractable: false` across the round trip — the key
 * comes back usable and still unreadable. That property is the entire reason
 * this feature can exist at all; there is no safe way to do it with a string.
 */
const persistKey = (vault: UnlockedVault): Promise<IDBValidKey> =>
  transact('readwrite', (store) => store.put(vault.masterKey, RECORD_KEY));

const readKey = (): Promise<CryptoKey | undefined> =>
  transact('readonly', (store) => store.get(RECORD_KEY) as IDBRequest<CryptoKey | undefined>);

/** The key and the lock mark together: a mark left behind would hold the
 *  next trusted unlock of this browser locked for no reason. */
const forgetKey = (): Promise<undefined> =>
  transact('readwrite', (store) => store.clear() as IDBRequest<undefined>);

const hold = (): Promise<IDBValidKey> =>
  transact('readwrite', (store) => store.put(true, HELD_KEY));

const release = (): Promise<undefined> =>
  transact('readwrite', (store) => store.delete(HELD_KEY) as IDBRequest<undefined>);

const isHeld = async (): Promise<boolean> =>
  (await transact('readonly', (store) => store.get(HELD_KEY) as IDBRequest<unknown>).catch(
    () => undefined
  )) === true;

export const isDeviceTrusted = async (): Promise<boolean> =>
  (await readKey().catch(() => undefined)) !== undefined;

/** Would a page load restore this vault without a password? A stored key that
 *  the user has not locked. Checked before anything asks the server. */
export const isRestorable = async (): Promise<boolean> =>
  (await isDeviceTrusted()) && !(await isHeld());
